import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Building2, LogIn, LogOut, RefreshCw, AlertCircle, Flame } from 'lucide-react';
import { AttendanceService } from './services/AttendanceService';
import BuildingView from './components/BuildingView';
import VisitorKiosk from './components/VisitorKiosk';
import FireDrill from './components/FireDrill';

// ─── Config ───────────────────────────────────────────────────────────────────
// Same SharePoint site and Azure AD app registration as the Schedule app.
// Add "http://localhost:3001" and "https://attendance.evokebehavioralhealth.com"
// as Redirect URIs in the Azure AD app registration (portal.azure.com).

const config = {
  siteUrl: 'https://evokebehavioralhealthcom.sharepoint.com/sites/Clinistrators',
  clientId: 'c9f70b7e-8ffb-403d-af93-80b95b38a0bb',
  tenantId: 'a4adcc38-7b4e-485c-80f9-7d9ca4e83d64',
  redirectUri: window.location.hostname === 'localhost'
    ? 'http://localhost:3001'
    : 'https://attendance.evokebehavioralhealth.com',

  // Azure AD group Object ID — only members of this group appear as staff.
  // Azure Portal → Azure AD → Groups → [group name] → Overview → Object ID
  staffGroupId: '97d98640-0624-469b-a344-2d300bc37b72',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDateLocal = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// localStorage key for kiosk-mode records (synced to SP when admin logs in)
const localKey = (date) => `evoke-attendance-local-${date}`;
const DRILL_KEY = 'evoke-fire-drill-session';
const AUTO_TIME_ZONE = 'America/Denver';
const STANDARD_CHECK_IN = '8:00 AM';
const STANDARD_CHECK_OUT = '4:00 PM';
const VISITOR_PERSON_TYPES = new Set(['visitor', 'parent', 'provider', 'other']);

const getCurrentDateInTimeZone = (timeZone) => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const pick = (type) => parts.find(p => p.type === type)?.value;
  return `${pick('year')}-${pick('month')}-${pick('day')}`;
};

const parseTimeTo24Hour = (timeValue) => {
  const raw = (timeValue || '').trim();
  if (!raw) return null;

  const twelveHour = raw.match(/^(0?[1-9]|1[0-2]):([0-5]\d)\s*([AP]M)$/i);
  if (twelveHour) {
    const hour12 = parseInt(twelveHour[1], 10);
    const minute = parseInt(twelveHour[2], 10);
    const meridiem = twelveHour[3].toUpperCase();
    const hour24 = meridiem === 'PM' ? (hour12 % 12) + 12 : (hour12 % 12);
    return { hour: hour24, minute };
  }

  const twentyFourHour = raw.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (twentyFourHour) {
    return {
      hour: parseInt(twentyFourHour[1], 10),
      minute: parseInt(twentyFourHour[2], 10),
    };
  }

  return null;
};

const getTimeZoneOffsetMs = (date, timeZone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtc - date.getTime();
};

const buildUtcFromTimeZoneDateAndClock = (dateStr, clock, timeZone) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const guessUtc = Date.UTC(year, month - 1, day, clock.hour, clock.minute, 0, 0);
  const guessDate = new Date(guessUtc);
  const offsetMs = getTimeZoneOffsetMs(guessDate, timeZone);
  return new Date(guessUtc - offsetMs);
};

const resolveStaffWindowForDate = (staffMember, dateStr) => {
  const absentAM = !!staffMember.absentAM;
  const absentPM = !!staffMember.absentPM;
  const absentFullDay = !!staffMember.absentFullDay;

  if (absentFullDay) return null;

  const arrivalClock = parseTimeTo24Hour(staffMember.absentAMArrivalTime || '');
  const departureClock = parseTimeTo24Hour(staffMember.absentPMDepartureTime || '');

  // If both sessions are absent and no explicit window exists, treat as full-day absent.
  if (absentAM && absentPM && !arrivalClock && !departureClock) return null;

  const checkInClock = absentAM && arrivalClock
    ? arrivalClock
    : parseTimeTo24Hour(STANDARD_CHECK_IN);

  const checkOutClock = absentPM && departureClock
    ? departureClock
    : parseTimeTo24Hour(STANDARD_CHECK_OUT);

  if (!checkInClock || !checkOutClock) return null;

  const checkInAt = buildUtcFromTimeZoneDateAndClock(dateStr, checkInClock, AUTO_TIME_ZONE);
  const checkOutAt = buildUtcFromTimeZoneDateAndClock(dateStr, checkOutClock, AUTO_TIME_ZONE);
  return { checkInAt, checkOutAt };
};

// ─── App ──────────────────────────────────────────────────────────────────────

const svc = new AttendanceService(config);

export default function App() {
  const today = formatDateLocal(new Date());
  const autoAttendanceInFlight = useRef(false);

  // ── Auth
  const [authState, setAuthState] = useState('loading'); // 'loading' | 'authenticated' | 'unauthenticated'
  const [userName, setUserName] = useState('');
  const [authError, setAuthError] = useState('');

  // ── Data
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [selectedDate, setSelectedDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState('');

  // ── UI
  const [activeView, setActiveView] = useState('building'); // 'building' | 'kiosk' | 'fire-drill'
  const [drillActive, setDrillActive] = useState(false);

  // ─── Auth bootstrap ──────────────────────────────────────────────────────

  useEffect(() => {
    svc.handleRedirect().then(account => {
      if (account) {
        setAuthState('authenticated');
        setUserName(account.name || account.username || '');
      } else {
        setAuthState('unauthenticated');
      }
    }).catch(err => {
      console.error('Auth error:', err);
      setAuthState('unauthenticated');
    });

    // Resume active fire drill across page reloads
    try {
      const raw = localStorage.getItem(DRILL_KEY);
      if (raw) {
        const drill = JSON.parse(raw);
        if (drill.date === formatDateLocal(new Date()) && !drill.endTime) {
          setDrillActive(true);
        }
      }
    } catch { /* ignore */ }
  }, []);

  // ─── Login / Logout ──────────────────────────────────────────────────────

  const handleLogin = async () => {
    setAuthError('');
    try {
      const account = await svc.login();
      setAuthState('authenticated');
      setUserName(account.name || account.username || '');
    } catch (err) {
      setAuthError(`Sign-in failed: ${err.message}`);
    }
  };

  const handleLogout = () => {
    svc.logout();
    setAuthState('unauthenticated');
    setUserName('');
    setStaff([]);
    setClients([]);
    setAttendanceRecords([]);
  };

  // ─── Data loading ────────────────────────────────────────────────────────

  const loadData = useCallback(async (date = selectedDate) => {
    if (!svc.isAuthenticated()) return;
    setLoading(true);
    setDataError('');
    try {
      const [staffData, clientData, spRecords] = await Promise.all([
        svc.loadStaff(),
        svc.loadClients(),
        svc.loadAttendanceForDate(date),
      ]);
      setStaff(staffData);
      setClients(clientData);

      // Merge SharePoint records with any locally-stored kiosk records
      const localRaw = localStorage.getItem(localKey(date));
      const localRecords = localRaw ? JSON.parse(localRaw) : [];

      // SP records take priority (they are the source of truth once synced)
      const spIds = new Set(spRecords.map(r => r.personId));
      const mergedLocal = localRecords.filter(r => !spIds.has(r.personId));
      setAttendanceRecords([...spRecords, ...mergedLocal]);
    } catch (err) {
      console.error('Data load error:', err);
      setDataError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // ─── Automatic staff attendance (MST schedule windows) ─────────────────

  const runAutoStaffAttendance = useCallback(async (dateStr) => {
    if (!svc.isAuthenticated()) return;
    if (autoAttendanceInFlight.current) return;
    autoAttendanceInFlight.current = true;

    try {
      const now = new Date();
      const recordsByPerson = new Map(attendanceRecords.map(r => [r.personId, r]));
      const standardCheckoutClock = parseTimeTo24Hour(STANDARD_CHECK_OUT);
      const standardCheckoutAt = standardCheckoutClock
        ? buildUtcFromTimeZoneDateAndClock(dateStr, standardCheckoutClock, AUTO_TIME_ZONE)
        : null;

      for (const staffMember of staff) {
        const email = (staffMember.email || '').toLowerCase();
        if (!email) continue;

        const attendanceWindow = resolveStaffWindowForDate(staffMember, dateStr);
        if (!attendanceWindow) continue; // full-day absence or insufficient schedule data

        const { checkInAt, checkOutAt } = attendanceWindow;
        const personId = `aad-email-${email}`;
        const currentRecord = recordsByPerson.get(personId);

        // Auto check-in when check-in time is reached and no record exists yet.
        if (!currentRecord && now >= checkInAt) {
          const checkInRecord = {
            personId,
            personName: staffMember.name,
            personType: 'staff',
            email,
            date: dateStr,
            checkInTime: checkInAt.toISOString(),
            checkOutTime: null,
            isCheckedIn: true,
            purpose: '',
            host: '',
            organization: '',
          };

          try {
            const saved = await svc.createCheckIn(checkInRecord);
            const merged = { ...saved, personId };
            recordsByPerson.set(personId, merged);
            setAttendanceRecords(prev => [
              ...prev.filter(r => r.personId !== personId),
              merged,
            ]);
            pruneLocalRecord(personId, dateStr);
          } catch (err) {
            console.error('Auto check-in save failed:', err);
            recordsByPerson.set(personId, checkInRecord);
            setAttendanceRecords(prev => [
              ...prev.filter(r => r.personId !== personId),
              checkInRecord,
            ]);
            saveLocalRecord(checkInRecord, dateStr);
          }
        }

        const refreshedRecord = recordsByPerson.get(personId);

        // Auto check-out at the window end when the person is still checked in.
        if (refreshedRecord?.isCheckedIn && now >= checkOutAt) {
          const checkedOut = {
            ...refreshedRecord,
            checkOutTime: checkOutAt.toISOString(),
            isCheckedIn: false,
          };

          if (refreshedRecord.spId) {
            try {
              await svc.updateCheckOut(refreshedRecord.spId, checkedOut.checkOutTime);
            } catch (err) {
              console.error('Auto check-out save failed:', err);
              saveLocalRecord(checkedOut, dateStr);
            }
          } else {
            saveLocalRecord(checkedOut, dateStr);
          }

          recordsByPerson.set(personId, checkedOut);
          setAttendanceRecords(prev => prev.map(r =>
            r.personId === personId
              ? checkedOut
              : r
          ));
        }
      }

      // Auto-check out all visitor-type records at standard 4:00 PM MST.
      if (standardCheckoutAt && now >= standardCheckoutAt) {
        for (const [personId, record] of recordsByPerson.entries()) {
          const personType = (record.personType || '').toLowerCase();
          if (!VISITOR_PERSON_TYPES.has(personType)) continue;
          if (!record.isCheckedIn) continue;

          const checkedOut = {
            ...record,
            checkOutTime: standardCheckoutAt.toISOString(),
            isCheckedIn: false,
          };

          if (record.spId) {
            try {
              await svc.updateCheckOut(record.spId, checkedOut.checkOutTime);
            } catch (err) {
              console.error('Visitor auto check-out save failed:', err);
              saveLocalRecord(checkedOut, dateStr);
            }
          } else {
            saveLocalRecord(checkedOut, dateStr);
          }

          recordsByPerson.set(personId, checkedOut);
          setAttendanceRecords(prev => prev.map(r =>
            r.personId === personId
              ? checkedOut
              : r
          ));
        }
      }
    } catch (err) {
      console.error('Auto staff attendance failed:', err);
    } finally {
      autoAttendanceInFlight.current = false;
    }
  }, [attendanceRecords, staff]);

  useEffect(() => {
    if (authState === 'authenticated') {
      loadData(selectedDate);
    }
  }, [authState, selectedDate, loadData]);

  useEffect(() => {
    if (authState !== 'authenticated') return;

    const run = () => {
      const currentMstDate = getCurrentDateInTimeZone(AUTO_TIME_ZONE);
      runAutoStaffAttendance(currentMstDate);
    };

    // Run once on auth/date changes, then poll every minute while app is open.
    run();
    const timerId = setInterval(run, 60 * 1000);

    return () => clearInterval(timerId);
  }, [authState, runAutoStaffAttendance]);

  // ─── Check-in ────────────────────────────────────────────────────────────

  /**
   * person shape:
   *   { personId, personName, personType, personId_sp?, role?, program?,
   *     purpose?, host?, organization? }
   */
  const handleCheckIn = useCallback(async (person) => {
    const now = new Date().toISOString();
    const record = {
      personId:     person.personId,
      personName:   person.personName,
      personType:   person.personType,
      email:        person.email || '',
      date:         selectedDate,
      checkInTime:  now,
      checkOutTime: null,
      isCheckedIn:  true,
      purpose:      person.purpose || '',
      host:         person.host || '',
      organization: person.organization || '',
    };

    // Optimistic UI update
    setAttendanceRecords(prev => [
      ...prev.filter(r => r.personId !== person.personId),
      record,
    ]);

    // Persist to SP
    try {
      const saved = await svc.createCheckIn(record);
      // Keep the original personId (used to match against the expected roster)
      // but pick up spId from SharePoint so checkout can update the right record
      setAttendanceRecords(prev => [
        ...prev.filter(r => r.personId !== person.personId),
        { ...saved, personId: record.personId },
      ]);
      // Prune from localStorage if it was a kiosk entry
      pruneLocalRecord(person.personId, selectedDate);
    } catch (err) {
      console.error('Check-in save failed:', err);
      // Fall back to localStorage so data isn't lost
      saveLocalRecord(record, selectedDate);
    }
  }, [selectedDate]);

  // ─── Check-out ───────────────────────────────────────────────────────────

  const handleCheckOut = useCallback(async (personId) => {
    const now = new Date().toISOString();

    // Optimistic UI update
    setAttendanceRecords(prev => prev.map(r =>
      r.personId === personId
        ? { ...r, checkOutTime: now, isCheckedIn: false }
        : r
    ));

    const record = attendanceRecords.find(r => r.personId === personId);
    if (!record) return;

    if (record.spId) {
      try {
        await svc.updateCheckOut(record.spId, now);
      } catch (err) {
        console.error('Check-out save failed:', err);
        saveLocalRecord({ ...record, checkOutTime: now, isCheckedIn: false }, selectedDate);
      }
    } else {
      // Local-only record — update in localStorage
      saveLocalRecord({ ...record, checkOutTime: now, isCheckedIn: false }, selectedDate);
    }
  }, [attendanceRecords, selectedDate]);

  // ─── Fire drill callbacks ─────────────────────────────────────────────────

  const onDrillStart = useCallback(() => setDrillActive(true), []);
  const onDrillEnd = useCallback(async (result) => {
    setDrillActive(false);
    if (svc.isAuthenticated()) {
      try { await svc.saveFireDrill(result); } catch { /* non-critical */ }
    }
  }, []);

  // ─── localStorage helpers ─────────────────────────────────────────────────

  const saveLocalRecord = (record, date) => {
    try {
      const raw = localStorage.getItem(localKey(date));
      const records = raw ? JSON.parse(raw) : [];
      const updated = [...records.filter(r => r.personId !== record.personId), record];
      localStorage.setItem(localKey(date), JSON.stringify(updated));
    } catch { /* ignore */ }
  };

  const pruneLocalRecord = (personId, date) => {
    try {
      const raw = localStorage.getItem(localKey(date));
      if (!raw) return;
      const records = JSON.parse(raw).filter(r => r.personId !== personId);
      localStorage.setItem(localKey(date), JSON.stringify(records));
    } catch { /* ignore */ }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (authState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600 text-lg">Loading…</span>
      </div>
    );
  }

  if (authState === 'unauthenticated') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-sm w-full text-center space-y-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
            <Building2 className="w-8 h-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Evoke Attendance</h1>
            <p className="text-gray-500 text-sm mt-2">Sign in with your Evoke Microsoft account to manage building attendance.</p>
          </div>
          {authError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm flex items-start gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {authError}
            </div>
          )}
          <button
            onClick={handleLogin}
            className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <LogIn className="w-5 h-5" /> Sign in with Microsoft
          </button>
        </div>
      </div>
    );
  }

  // ── Authenticated layout
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top nav */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            {/* Logo + title */}
            <div className="flex items-center gap-2 font-bold text-gray-800">
              <Building2 className="w-5 h-5 text-blue-600" />
              <span>Evoke Attendance</span>
            </div>

            {/* Tab nav */}
            <nav className="flex gap-1 h-full">
              {[
                { id: 'building',   label: 'Building View' },
                { id: 'kiosk',      label: 'Visitor Kiosk' },
                { id: 'fire-drill', label: 'Fire Drill',   alert: drillActive },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveView(tab.id)}
                  className={`px-4 h-full border-b-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    activeView === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  } ${tab.alert ? 'text-red-600' : ''}`}
                >
                  {tab.id === 'fire-drill' && <Flame className="w-4 h-4" />}
                  {tab.label}
                  {tab.alert && (
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  )}
                </button>
              ))}
            </nav>

            {/* User / sign out */}
            <div className="flex items-center gap-3">
              {userName && (
                <span className="text-xs text-gray-500 hidden sm:block">{userName}</span>
              )}
              <button
                onClick={() => loadData(selectedDate)}
                title="Refresh"
                className="p-1.5 text-gray-400 hover:text-gray-700 transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Error banner */}
      {dataError && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-3 text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {dataError}
          <button onClick={() => setDataError('')} className="ml-auto text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {loading && !attendanceRecords.length ? (
          <div className="flex justify-center items-center py-20">
            <RefreshCw className="w-7 h-7 animate-spin text-blue-500" />
            <span className="ml-3 text-gray-500">Loading data from SharePoint…</span>
          </div>
        ) : (
          <>
            {activeView === 'building' && (
              <BuildingView
                staff={staff}
                clients={clients}
                attendanceRecords={attendanceRecords}
                selectedDate={selectedDate}
                today={today}
                onDateChange={setSelectedDate}
                onCheckIn={handleCheckIn}
                onCheckOut={handleCheckOut}
                onRefresh={() => loadData(selectedDate)}
                loading={loading}
              />
            )}
            {activeView === 'kiosk' && (
              <VisitorKiosk
                attendanceRecords={attendanceRecords}
                today={today}
                onCheckIn={handleCheckIn}
                onCheckOut={handleCheckOut}
                onSwitchToBuilding={() => setActiveView('building')}
              />
            )}
            {activeView === 'fire-drill' && (
              <FireDrill
                attendanceRecords={attendanceRecords}
                today={today}
                onDrillStart={onDrillStart}
                onDrillEnd={onDrillEnd}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}
